import { useCallback, useState } from 'react';
import {
  Accordion,
  AccordionItem,
  type AccordionItemProps,
} from '@carbon/react';

import { formatCount } from '../utils';
import { AssetListPanel, type AssetListPanelProps } from './asset-list-panel';
import styles from './select-source-step.module.scss';

type RenderToggle = NonNullable<AccordionItemProps['renderToggle']>;

/**
 * "Add tracts by list", beside Map Query and Existing Package: the Asset List
 * source's paste box, file and check, collapsed until opened. The matched names
 * go with the source at Next and arrive ticked; the title says how many matched.
 * A name check that fails opens it, so the reason Next is shut shows; it stays
 * open once the check passes.
 *
 * @param {AssetListPanelProps} props - The asset list panel's input, handlers and check state
 * @return {JSX.Element} The collapsed section
 */
export function AddTractsByList(props: AssetListPanelProps) {
  const { failure } = props;
  // Whether the section is open. Carbon keeps its own state and follows this
  // one each time it changes, so this follows every way the user opens and
  // closes it too.
  const [open, setOpen] = useState(failure !== null);
  const [shownFailure, setShownFailure] = useState(failure);
  if (failure !== shownFailure) {
    setShownFailure(failure);
    if (failure !== null) setOpen(true);
  }
  // Carbon's heading, also telling of Escape, with which Carbon closes the
  // section without a word.
  const renderToggle = useCallback<RenderToggle>(
    ({ onKeyDown, ...toggle }) => (
      <button
        {...toggle}
        type="button"
        onKeyDown={(event) => {
          onKeyDown?.(event);
          if (event.key === 'Escape') setOpen(false);
        }}
      />
    ),
    []
  );

  const added = props.summary.matched.length;
  const title =
    added > 0
      ? `Add tracts by list (${formatCount(added)} added)`
      : 'Add tracts by list (optional)';
  return (
    <Accordion align="start" className={styles.listSection}>
      <AccordionItem
        title={title}
        open={open}
        onHeadingClick={({ isOpen }) => setOpen(isOpen)}
        renderToggle={renderToggle}
      >
        <div className={styles.listBody}>
          <AssetListPanel {...props} />
        </div>
      </AccordionItem>
    </Accordion>
  );
}
